import { getExchangeRate } from "@/common/utilities/math/asConvertedCurrency";
import { minorUnitFactor } from "@/common/utilities/money/currencyPrecision";
import { toRoundNumber } from "@/common/utilities/money/filterBounds";

/**
 * The range of the lead form's "maximum monthly budget" slider, anchored in EUR
 * and scaled into the currency the enquirer is actually quoted in.
 *
 * The slider used to carry one hard-coded range -- 50,000 to 2,000,000 minor
 * units, step 5,000 -- written for a two-decimal currency. Read as euros that
 * is the intended EUR 500 to EUR 20,000; read as anything else it is whatever
 * those digits happen to mean there. A Kuwaiti office (three decimals) topped
 * out at KWD 2,000, roughly EUR 6,000, so an enquirer could not express a
 * budget for most of the offices on the page. A Japanese one (no decimals)
 * started at JPY 50,000, a tenth of a realistic floor, and stepped through the
 * range in JPY 5,000 increments.
 *
 * Deriving the range from the live rate means the slider spans the same real
 * money everywhere, and a new market works the day its first location lists --
 * the same reasoning as the price filters in `filterBounds`, which this shares
 * its rounding with.
 */
export interface BudgetBounds {
    /** All three in the currency's minor unit, as the API and slider want. */
    min: number;
    max: number;
    step: number;
}

/**
 * The largest budget that survives the trip to storage unchanged.
 *
 * `lead_inquiry.maxBudget` is a bigint, so the column itself is not the
 * constraint -- it holds far more than any currency needs. The binding limit
 * is JavaScript: the budget crosses the API as a JSON number and is a JS
 * number at both ends, so past 2^53 it stops being an exact integer and would
 * be quietly rounded to something the enquirer did not pick.
 *
 * That leaves roughly a thousandfold of headroom over the weakest currency in
 * the list, so this never binds in practice -- it is here so a rate that has
 * gone haywire cannot put a figure on the slider that means something else by
 * the time it lands.
 *
 * It used to be the `integer` column's 2,147,483,647, which did bind: EUR
 * 20,000 is 2,000,000 centimes and fits easily, but the same money is 210
 * billion Lebanese piastres and does not. Nine of the ninety-two operating
 * currencies scaled past it, four of them (LBP, IDR, IQD, KHR) far enough
 * that even the EUR 500 floor would not fit, and Postgres rejects an
 * over-wide integer outright -- so those leads could not be written at all.
 */
export const MAX_STORABLE_BUDGET = Number.MAX_SAFE_INTEGER;

/** The same three figures in whole euros, before any currency is applied. */
export interface BudgetAnchor {
    min: number;
    max: number;
    step: number;
}

/**
 * The anchor. Its ceiling matches the private-office price filter's EUR 20,000
 * (`OFFICE_TOTAL_EUR_BOUNDS`) so a budget can cover the dearest office a
 * visitor can filter for, over a floor of EUR 500 and in steps fine enough to
 * land on a real figure rather than a bracket.
 */
export const BUDGET_EUR_ANCHOR: BudgetAnchor = {
    min: 500,
    max: 20000,
    step: 50,
};

/**
 * Scales the EUR anchor into `currency`'s minor units at `rate`.
 *
 * Rounding happens in major units, where the round numbers are: the step and
 * floor are rounded to something a slider can sensibly step through, the
 * ceiling is then snapped onto that step grid so the top of the range is
 * actually reachable, and only then is the whole thing multiplied into minor
 * units. Going the other way -- scaling the minor-unit figures directly --
 * rounds to the nearest cent-sized quantity, which is meaningless on a monthly
 * office rent.
 */
export const scaleBudgetBounds = (
    anchor: BudgetAnchor,
    rate: number,
    currency: string
): BudgetBounds => {
    const factor = minorUnitFactor(currency);
    // The whole range has to stay inside the column, so the rate is capped
    // before anything is derived from it rather than the result trimmed after
    // -- trimming the ceiling alone would leave a floor above it.
    const ceiling = Math.floor(MAX_STORABLE_BUDGET / factor);
    const storableRate = Math.min(rate, ceiling / anchor.max);

    // Nobody picks a monthly office budget to the cent, so the step never goes
    // below a whole major unit however weak the currency.
    const step = Math.max(1, toRoundNumber(anchor.step * storableRate));
    // Keeping the floor on the step grid too means every value the thumb can
    // reach is a round one, not just the offsets from an odd starting point.
    const min = Math.max(
        step,
        Math.round(toRoundNumber(anchor.min * storableRate) / step) * step
    );
    // Snapping the ceiling to the grid rounds to nearest, which can land a
    // step above what the column takes, so the storable ceiling is a floor
    // division and wins.
    const steps = Math.min(
        Math.round((anchor.max * storableRate - min) / step),
        Math.floor((ceiling - min) / step)
    );
    const max = Math.max(min + step, min + steps * step);

    return { min: min * factor, max: max * factor, step: step * factor };
};

/**
 * The budget slider's bounds for `currency`, scaled from the EUR anchor at the
 * current rate.
 *
 * With no rate available the anchor's own figures are used, converted into the
 * currency's minor unit. The magnitude is then wrong -- it is a euro range
 * wearing another currency's symbol -- but it is bounded, on-step and submits a
 * value the backend accepts, which beats a slider that cannot be moved. The
 * minor-unit conversion still applies in that case: it depends on the currency,
 * not on the rate, so there is no reason to get it wrong as well.
 */
export const budgetBoundsForCurrency = async (
    currency: string
): Promise<BudgetBounds> => {
    const rate =
        currency === "EUR" ? 1 : await getExchangeRate("EUR", currency);
    return scaleBudgetBounds(BUDGET_EUR_ANCHOR, rate ?? 1, currency);
};

/**
 * Pulls a previously chosen budget into `bounds`, for when the rate resolves
 * after the enquirer has already moved the slider.
 *
 * A range input silently clamps a value outside its range without telling
 * React, so the thumb would sit at one end while state still held the old
 * number -- and that stale number, not the one on screen, is what the form
 * submits. Snapping to the step grid keeps it on a value the thumb can return
 * to after being dragged away.
 */
export const clampBudget = (value: number, bounds: BudgetBounds): number => {
    const stepped =
        bounds.min +
        Math.round((value - bounds.min) / bounds.step) * bounds.step;
    return Math.min(bounds.max, Math.max(bounds.min, stepped));
};
